"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDomainSearch = void 0;
var _react = require("react");
var _domainServices = require("@site/domain-services");
var _patternLibraryUtils = require("@site/pattern-library-utils");
function getISC() {
  // copies behavior from serp-app repo
  const urlQueryParams = new URLSearchParams(window?.location?.search);
  return urlQueryParams.get('isc') || (0, _patternLibraryUtils.getCookieValue)('isc') || (0, _patternLibraryUtils.getCookieValue)('aff_isc') || '';
}
const getMarket = () => {
  return (0, _patternLibraryUtils.getCookieValue)('market') || 'en-US';
};

/**
 * useDomainSearch
 * Custom hook for fetching domain search results with DomainServices.
 *
 * @param {string} searchQuery The query string used for the domain search.
 * @param {string} apiKey The API key used for authentication.
 * @param {string} plid A plid with 1 as GoDaddy everything > 0 being a reseller
 * @param {string} env The environment setting (e.g., 'prod' for production).
 * @param {string} market The market setting for local results (e.g., 'en-US' for English, United States).
 * @param {String} isc ISC code for discounts.
 * @param {String} itc ITC to inject for tracking purposes.
 * @param {String} maxSpins Spin Domains per page to display.
 * @returns {Object} Search Data
 */

// eslint-disable-next-line max-params
const useDomainSearch = (searchQuery, serp, domainServicesProps = {}) => {
  const {
    apiKey,
    plid,
    env = (0, _patternLibraryUtils.getDefaultEnv)(),
    market = getMarket(),
    itc,
    maxSpins
  } = serp || {};
  const [searchData, setSearchData] = (0, _react.useState)({
    exactResults: null,
    spinResults: null,
    error: null,
    hasSpins: false
  });
  const [isLoading, setIsLoading] = (0, _react.useState)(false);
  (0, _react.useEffect)(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return;
    }
    if (searchData[searchQuery]) {
      // If the search data already exists, don't make another fetch call
      return;
    }
    setIsLoading(true);
    const makeFetchCall = async () => {
      try {
        const domainServices = new _domainServices.DomainServices({
          fetchEntity: window.fetch,
          apiKey: apiKey,
          plid: plid,
          env: env,
          market: market,
          ...domainServicesProps
        });
        const extraParams = {
          partial_query: searchQuery,
          extravalue: 'important',
          pagesize: maxSpins || 5,
          isc: getISC(),
          itc: itc
        };
        const findExactResults = await domainServices.findExact({
          query: searchQuery,
          extraParams
        });
        const findSpinResults = await domainServices.findSpins({
          query: searchQuery,
          extraParams
        });
        setSearchData(prevState => ({
          ...prevState,
          [searchQuery]: {
            exactResults: findExactResults,
            spinResults: findSpinResults,
            hasSpins: findSpinResults?.Products != null,
            error: null
          }
        }));
      } catch (err) {
        setSearchData(prevState => ({
          ...prevState,
          [searchQuery]: {
            ...prevState[searchQuery],
            error: err.message
          }
        }));
      } finally {
        setIsLoading(false);
      }
    };
    if (searchQuery) {
      makeFetchCall();
    }
  }, [searchData, searchQuery, apiKey, plid, env, market, itc]);
  return {
    searchData,
    isLoading
  };
};
exports.useDomainSearch = useDomainSearch;